import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, fileUrl, formatApiError } from "../lib/api";
import { CharacterProfileDisplay, hasProfileContent } from "../components/app/CharacterProfile";
import { SkullRating } from "../components/app/SkullRating";
import { Skull, Printer, MapPin, Users, Loader2, ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react";

export default function CharacterSheet() {
  const { slug, rid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null); // index of open image, or null

  useEffect(() => {
    axios.get(`${API}/public/character/${slug}/${rid}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(formatApiError(e)));
  }, [slug, rid]);

  const imgs = data?.role?.reference_images || [];
  const stepLb = (dir) => setLightbox((i) => (i === null ? i : (i + dir + imgs.length) % imgs.length));

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowLeft") setLightbox((i) => (i === null ? i : (i - 1 + imgs.length) % imgs.length));
      else if (e.key === "ArrowRight") setLightbox((i) => (i === null ? i : (i + 1) % imgs.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, imgs.length]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#F2EBE1] flex items-center justify-center px-6">
        <div className="text-center" data-testid="character-sheet-error">
          <Skull size={32} className="text-blood mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-display text-2xl uppercase tracking-tight">Character Sheet Not Found</h1>
          <p className="text-sm text-[#A19D94] mt-2">This character profile link is invalid or no longer available.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-blood" size={28} />
      </div>
    );
  }

  const role = data.role || {};

  return (
    <div className="min-h-screen bg-[#050505] text-[#F2EBE1] py-10 px-6 print:py-0 print:px-0">
      <div className="max-w-3xl mx-auto">
        {/* Brand + actions */}
        <div className="flex items-center justify-between mb-8 no-print">
          <div className="inline-flex items-center gap-3">
            <Skull size={24} className="text-blood" strokeWidth={1.5} />
            <span className="font-display text-xl font-bold tracking-[0.04em] text-blood">SCARE AUDITIONS<span className="text-[0.45em] font-semibold tracking-normal align-top opacity-80 ml-1">.co.uk</span></span>
          </div>
          <button onClick={() => window.print()} data-testid="character-sheet-print-btn"
            className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] transition-colors">
            <Printer size={14} /> Print / Download PDF
          </button>
        </div>

        <div className="print-area" data-testid="character-sheet">
          {/* Header */}
          <div className="border-b-2 border-blood pb-5 mb-6">
            <div className="label-mini text-blood">{data.event?.name || "Character Sheet"}</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight mt-1.5" data-testid="character-sheet-role-name">{role.name}</h1>
            {role.character_name && <p className="text-lg text-[#F2EBE1] mt-1">{role.character_name}</p>}
            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-sm text-[#A19D94]">
              {role.department && <span className="flex items-center gap-1.5"><Users size={14} className="text-blood" /> {role.department}</span>}
              {role.placement && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-blood" /> {role.placement}</span>}
            </div>
            {role.skill_level > 0 && <SkullRating value={role.skill_level} size={20} className="mt-3" />}
          </div>

          {/* Reference image gallery */}
          {imgs.length > 0 && (
            <div className="mb-7">
              <h4 className="font-display text-sm uppercase tracking-wider text-blood mb-2.5 flex items-center gap-2"><ImageIcon size={14} /> Reference Images</h4>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {imgs.map((id, i) => (
                  <button key={id} type="button" onClick={() => setLightbox(i)} data-testid={`character-sheet-img-${id}`}
                    className="group relative block w-full aspect-square overflow-hidden border border-[#262626] hover:border-blood transition-colors cursor-zoom-in">
                    <img src={fileUrl(id)} alt="reference" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Character profile sections */}
          {hasProfileContent(role) ? (
            <CharacterProfileDisplay role={role} />
          ) : (
            <p className="text-sm text-[#6B6862] italic">No character details have been added yet.</p>
          )}
        </div>
      </div>

      {/* Lightbox gallery */}
      {lightbox !== null && imgs[lightbox] && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center no-print" data-testid="character-sheet-lightbox"
          onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} data-testid="lightbox-close"
            className="absolute top-4 right-4 text-[#A19D94] hover:text-white p-2" aria-label="Close">
            <X size={26} />
          </button>
          {imgs.length > 1 && (
            <span className="absolute top-5 left-5 text-xs font-mono text-[#A19D94]" data-testid="lightbox-counter">{lightbox + 1} / {imgs.length}</span>
          )}
          {imgs.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); stepLb(-1); }} data-testid="lightbox-prev"
              className="absolute left-2 sm:left-6 text-[#A19D94] hover:text-white p-2" aria-label="Previous">
              <ChevronLeft size={40} />
            </button>
          )}
          <img src={fileUrl(imgs[lightbox])} alt="reference enlarged" onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] max-w-[90vw] object-contain border border-[#262626] shadow-2xl" data-testid="lightbox-image" />
          {imgs.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); stepLb(1); }} data-testid="lightbox-next"
              className="absolute right-2 sm:right-6 text-[#A19D94] hover:text-white p-2" aria-label="Next">
              <ChevronRight size={40} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
